import { Box, Grid, Toolbar, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Typography, Chip } from "@mui/material";
import update from "immutability-helper";
import _ from "lodash";
import moment from "moment";
import { useCallback, useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getFilters, removeFilter, saveFilter } from "../../../services/filter.service";
import { setFilterDateRange, setLockTotal, setSelectedFilter } from "../../../store/reducers/community-transfer.slice";
import { setLockTotal as setLockTotalComparison, setSelectedFilter as setSelectedFilterComparison } from "../../../store/reducers/comparisonReducers/communityTransferComparison.slice";
import { setFacilityIds, setMultipleFacility } from "../../../store/reducers/permission.slice";
import { ADD_NOTIFICATION } from "../../../store/types";
import { DASHBOARD_FILTER_TYPE } from "../../../types/common.type";
import { PAGE_TYPE } from "../../../types/pages.type";
import { calcProPercentsBasedOnFilterAndDays } from "../../../utilis/common";
import DateFilterComponent from "../../../components/dashboard/filter/DateFilterComponent";
import DashboardHeader from "../../../components/dashboard/header/DashboardHeader";
import CensusAverageBox from "../../../components/dashboard/shared/CensusAverageBox/CensusAverageBox";
import DashboardTotalCount from "../../../components/dashboard/shared/DashboardTotalCount";
import HeaderFilterOptions from "../../../components/dashboard/shared/headerFilterOptions/HeaderFilterOptions";
import HeaderMenu from "../../../components/shared/header-menu";
import CommunityHeaderFilterList from "./CommunityHeaderFilterList";
import FilterListIcon from '@mui/icons-material/FilterList';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import useFacilityId from "../../../hooks/useFacilityId";

const CommunityTransferHeader = ({
	handleFilterUpdate,
	loading,
	setLoading,
	setLoadingComparison,
	projectionDays,
	isComparingAgainstAvgCensus,
	percentageAgainst,
	filterListData,
	dynamicCards,
	isLayoutMode = false,
	disableInteractions = false,
	isHeaderVisible = true,
	setIsHeaderVisible,
	isMobile = false,
	isDataFromCache = false,
	pageConverterPatientList = [],
	pageConverterFilter = null,
	renderCardFilterPopover,
	interactiveHeaderFilterPopoverSlowTransition = false,
}) => {
	const dispatch = useDispatch();
	const [selectedFilterMenu, setSelectedFilterMenu] = useState("all");
	const [mobileFilterMenuAnchor, setMobileFilterMenuAnchor] = useState(null);
	const { dbData, cardFilter, filter, transferType, filterTotal, lockedTotalBy } = useSelector(
		(state) => state.communityTransfer
	);
	const { lockedTotal: lockedTotalComparison, filterTotal: filterTotalComparison } = useSelector(
		(state) => state.communityTransferComparison
	);
	const { facilityIds, isMultipleFacility } = useSelector((state) => state.permission);
	const { facilities: activeFacilities, prevStateFacilities } = useSelector((state) => state.activeFacilities);
	const { facilityId } = useFacilityId();
	const { isAutomaticReportSaved = false } = dbData;

	const { censusAverage } = dbData;
	const { percentageBy } = useSelector((state) => state.common);
	const [filterValue, setFilterValue] = useState("");
	const [filterOptions, setFilterOptions] = useState([]);
	const { lockedTotal } = useSelector((state) => state.communityTransfer);

	const totalFilterForViewer = calcProPercentsBasedOnFilterAndDays(
		filterTotal,
		filter,
		projectionDays,
		true,
		(!lockedTotalBy && !isComparingAgainstAvgCensus) || (lockedTotalBy && lockedTotalBy !== "census")
	);

	// Determine if default should be shown
	const shouldShowDefault = useMemo(() => {
		const isDefaultCondition =
			!transferType ||
			(Array.isArray(transferType) && transferType.length === 0) ||
			(typeof transferType === 'string' && transferType.trim() === '');

		const hasPriorityData = cardFilter?.priorityData?.length > 0 || cardFilter?.mainPriorityData?.length > 0;
		const isLockedByCensus = lockedTotal && lockedTotalBy === 'census';

		return (isDefaultCondition && !hasPriorityData) || isLockedByCensus;
	}, [transferType, cardFilter?.priorityData?.length, cardFilter?.mainPriorityData?.length, lockedTotal, lockedTotalBy]);

	const toggleHeader = useCallback(() => {
		setIsHeaderVisible(prev => {
			const newState = !prev;
			return newState;
		});
	}, [setIsHeaderVisible]);

	const handleChangeSelect = useCallback(
		(event) => {
			const selectedValue = event.target.value;
			if (selectedValue && selectedValue.facilityIds && selectedValue.facilityIds.length > 1) {
				dispatch(setFacilityIds(selectedValue.facilityIds));
				dispatch(setMultipleFacility(true));
			} else {
				dispatch(setFacilityIds([]));
				dispatch(setMultipleFacility(false));
			}

			handleFilterUpdate(true);
			let latestValues = {
				...selectedValue.filter,
				filter: {
					...selectedValue.filter.filter,
					startDate: moment(selectedValue.filter.filter.startDate).toDate(),
					endDate: moment(selectedValue.filter.filter.endDate).toDate(),
				},
			};
			setSelectedFilterMenu({ ...selectedValue, filter: latestValues });
			dispatch(setSelectedFilter({ ...selectedValue, filter: latestValues }));
		},
		[dispatch, handleFilterUpdate]
	);

	const getFiltersData = useCallback(() => {
		getFilters({ type: DASHBOARD_FILTER_TYPE.communityTransfer }).then((res) => {
			if (res && res.length > 0) {
				setFilterOptions(res);
			}
		});
	}, []);

	useEffect(() => {
		if (facilityId) {
			getFiltersData();
		}
	}, [getFiltersData, facilityId]);

	const handleClearFilter = useCallback(() => {
		setSelectedFilterMenu("all");
		dispatch(setSelectedFilter("all"));
		dispatch(setSelectedFilterComparison("all"));
	}, [dispatch]);

	useEffect(() => {
		// Skip reset if prevStateFacilities is empty (initial state) - this is first load, not a facility change
		const isInitialLoad = prevStateFacilities.length === 0 && activeFacilities.length > 0;
		
		// Check if cache exists for current filter to prevent unnecessary reset
		const hasCache = isDataFromCache;
		
		if (!_.isEqual(activeFacilities, prevStateFacilities)) {
			// Skip reset on initial load (prevStateFacilities is empty) or when cache exists
			if (!isInitialLoad && !hasCache) {
				handleClearFilter();
			}
		}
	}, [activeFacilities, handleClearFilter, prevStateFacilities, isDataFromCache]);

	const handleRemoveFilter = async (selectedItem) => {
		await removeFilter(selectedItem._id);
		const latestFacilityList = update(filterOptions, {
			$splice: [[filterOptions.indexOf(selectedItem), 1]],
		});
		setFilterOptions(latestFacilityList);
		return true;
	};

	// Mobile filter menu handlers
	const handleMobileFilterMenuOpen = (event) => {
		setMobileFilterMenuAnchor(event.currentTarget);
	};

	const handleMobileFilterMenuClose = () => {
		setMobileFilterMenuAnchor(null);
	};

	const handleMobileFilterItemClick = (item) => {
		// Here you can add logic to handle filter item clicks
		// For now, just close the menu
		handleMobileFilterMenuClose();
	};

	// Get filter items for mobile menu
	const getMobileFilterItems = () => {
		const items = [];

		// Add default percentage item
		if (shouldShowDefault) {
			items.push({
				label: percentageBy,
				type: "label",
				isMain: false,
				isDefault: true,
				isLocked: lockedTotal && lockedTotalBy === 'census'
			});
		}

		// Add main filter items based on cardFilter
		if (cardFilter) {
			// Check for all possible filter types
			const filterTypes = [
				{ key: 'totalMain', label: 'Total Main', isMain: true },
				{ key: 'doctorData', label: 'Doctor Data' },
				{ key: 'insuranceData', label: 'Insurance Data' },
				{ key: 'floorsData', label: 'Floors Data' },
				{ key: 'sixtyDaysData', label: 'Sixty Days Data' },
				{ key: 'daysData', label: 'Days Data' },
				{ key: 'safeDischargeAssLivData', label: 'Safe Discharge Ass Liv Data' },
				{ key: 'snfFacilityData', label: 'SNF Facility Data' }
			];

			filterTypes.forEach(filterType => {
				const filterData = cardFilter[filterType.key];
				if (filterData && Array.isArray(filterData) && filterData.length > 0) {
					items.push({
						label: filterType.label,
						type: "label",
						dataKey: filterType.key,
						isMain: filterType.isMain || false,
						isDefault: true,
						count: filterData.length,
						isLocked: lockedTotal && lockedTotalBy === filterType.key
					});
				}
			});

			// Add any other dynamic filters
			Object.keys(cardFilter).forEach(key => {
				if (!['totalMain', 'doctorData', 'daysData', 'insuranceData', 'floorsData', 'sixtyDaysData', 'safeDischargeAssLivData', 'snfFacilityData', 'priorityData', 'mainPriorityData'].includes(key)) {
					const filterData = cardFilter[key];
					if (filterData && Array.isArray(filterData) && filterData.length > 0) {
						items.push({
							label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
							type: "label",
							dataKey: key,
							isMain: false,
							isDefault: false,
							count: filterData.length,
							isLocked: lockedTotal && lockedTotalBy === key
						});
					}
				}
			});
		}

		return items;
	};

	const handleSaveFilter = async () => {
		let facilityIdsData = [];

		if (isMultipleFacility) {
			facilityIdsData = facilityIds;
		} else {
			facilityIdsData = facilityId ? [facilityId] : [];
		}

		const resData = await saveFilter({
			type: DASHBOARD_FILTER_TYPE.communityTransfer,
			name: filterValue,
			facilityIds: facilityIdsData,
			filter: { cardFilter, filter, transferType },
		});

		if (resData && resData.status === 200) {
			getFiltersData();
			setFilterValue("");
			return true;
		} else {
			dispatch({
				type: ADD_NOTIFICATION,
				payload: {
					label: resData.error,
					type: "error",
					id: "communitySaveFilterError",
				},
			});
			return false;
		}
	};

	const onChangeFilter = useCallback(
		(value) => {
			dispatch(setFilterDateRange(value));
		},
		[dispatch]
	);

	const handleSetDefaultTotal = useCallback(() => {
		dispatch(setLockTotal(lockedTotal ? null : filterTotal));
		dispatch(setLockTotalComparison(lockedTotalComparison ? null : filterTotalComparison));
	}, [dispatch, filterTotal, filterTotalComparison, lockedTotal, lockedTotalComparison]);

	return (
		<>
			{/* Mobile Filter Toggle Button - Only visible on mobile */}
			{isMobile && (
				<Box sx={{
					position: "fixed",
					top: "66px", // Position below the main header
					right: "10px",
					zIndex: 900, // Higher than Header (1200) and DashboardHeader (500)
				}}>
					<IconButton
						onClick={toggleHeader}
						sx={{
							backgroundColor: "rgba(99, 101, 120, 0.9)",
							color: "white",
							width: "25px",
							height: "25px",
							"&:hover": {
								backgroundColor: "rgba(99, 101, 120, 1)"
							},
							boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
							border: "2px solid rgba(255,255,255,0.2)"
						}}
					>
						{isHeaderVisible ? <KeyboardArrowUpIcon /> : <FilterListIcon />}
					</IconButton>
				</Box>
			)}

			{/* Mobile Filter Menu */}
			<Menu
				anchorEl={mobileFilterMenuAnchor}
				open={Boolean(mobileFilterMenuAnchor)}
				onClose={handleMobileFilterMenuClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'left',
				}}
				sx={{
					display: { xs: 'block', md: 'none' },
					'& .MuiPaper-root': {
						minWidth: '250px',
						maxWidth: '300px',
						maxHeight: '400px',
						overflow: 'auto'
					}
				}}
			>
				<Typography variant="subtitle2" sx={{ px: 2, py: 1, fontWeight: 'bold', borderBottom: '1px solid #e0e0e0' }}>
					Filter Options
				</Typography>
				{getMobileFilterItems()?.map((item, index) => (
					<MenuItem
						dense
						key={index}
						onClick={() => handleMobileFilterItemClick(item)}
						sx={{
							py: 1,
							px: 2,
							'&:hover': {
								backgroundColor: '#f5f5f5'
							}
						}}
					>
						<ListItemIcon sx={{ minWidth: 36 }}>
							<FilterListIcon sx={{ fontSize: 20, color: item.isLocked ? '#999' : '#4879f5' }} />
						</ListItemIcon>
						<ListItemText
							primary={item.label}
							primaryTypographyProps={{
								fontSize: '14px',
								color: item.isLocked ? '#999' : 'inherit',
								fontWeight: item.isMain ? 600 : 400
							}}
						/>
						{item.count > 0 && (
							<Chip
								label={item.count}
								size="small"
								sx={{
									backgroundColor: '#e3f2fd',
									color: '#4879f5',
									fontSize: '10px',
									height: '20px'
								}}
							/>
						)}
					</MenuItem>
				))}
			</Menu>

			<DashboardHeader isHeaderVisible={isHeaderVisible}>
				<Box
					display={"flex"}
					sx={{
						width: "100%",
						height: isMobile ? "40px" : "60px",
						backgroundColor: "#636578",
						display: "flex",
						alignItems: "center",
						padding: "0px 16px",
						cursor: disableInteractions ? "default" : "pointer",
						pointerEvents: disableInteractions ? "none" : "auto",
						opacity: disableInteractions ? 0.5 : 1,
						"@media (max-width: 768px)": {
							height: "45px",
							flexDirection: "column",
							alignItems: "center",
							gap: "4px"
						}
					}}
				>
					<DashboardTotalCount
						isLockedTotal={lockedTotal ? true : false}
						filterTotal={totalFilterForViewer}
						handleChange={disableInteractions ? undefined : handleSetDefaultTotal}
						background="linear-gradient(249.91deg, #4CEBEB 28.23%, #076673 76.55%)"
						percentageBy={percentageBy}
						cardFilter={cardFilter}
						disabled={disableInteractions}
						onMobileClick={handleMobileFilterMenuOpen}
						isMobile={isMobile}
						filterListData={filterListData}
					/>
					{!isMobile && (
						<Grid
							container
							direction={"row"}
							justifyContent="space-between"
							alignItems="center"
							sx={{ ml: "-10px", mt: "10px" }}
						>
							<Grid item lg={8} md={6} xs={12}>
								<CommunityHeaderFilterList
									filterListData={filterListData}
									cardFilter={cardFilter}
									transferType={transferType}
									percentageBy={percentageBy}
									lockedTotal={lockedTotal}
									lockedTotalBy={lockedTotalBy}
									dynamicCards={dynamicCards}
									disableInteractions={disableInteractions}
									renderCardFilterPopover={renderCardFilterPopover}
									interactiveHeaderFilterPopoverSlowTransition={interactiveHeaderFilterPopoverSlowTransition}
								/>
							</Grid>

							<Grid item lg={4} md={6} xs={12}>
								<HeaderFilterOptions
									selectedFilterMenu={selectedFilterMenu}
									handleChangeSelect={disableInteractions ? undefined : handleChangeSelect}
									filterOptions={filterOptions}
									filterValue={filterValue}
									setFilterValue={disableInteractions ? undefined : setFilterValue}
									handleRemoveFilter={disableInteractions ? undefined : handleRemoveFilter}
									handleSaveFilter={disableInteractions ? undefined : handleSaveFilter}
									handleClearFilter={disableInteractions ? undefined : handleClearFilter}
									disabled={disableInteractions}
									isMobile={isMobile}
								/>
							</Grid>
						</Grid>
					)}
					{isMobile && (
						<CommunityHeaderFilterList
							filterListData={filterListData}
							cardFilter={cardFilter}
							transferType={transferType}
							percentageBy={percentageBy}
							lockedTotal={lockedTotal}
							lockedTotalBy={lockedTotalBy}
							dynamicCards={dynamicCards}
							disableInteractions={disableInteractions}
							isMobile={isMobile}
							renderCardFilterPopover={renderCardFilterPopover}
							interactiveHeaderFilterPopoverSlowTransition={interactiveHeaderFilterPopoverSlowTransition}
						/>
					)}
				</Box>
				<Toolbar sx={{
					height: 91,
					"@media (max-width: 768px)": {
						height: "auto",
						minHeight: "auto",
						padding: "1px 4px",
						flexDirection: "column",
						alignItems: "stretch",
						gap: "12px"
					}
				}}>
					<Grid
						container
						direction={"row"}
						spacing={isMobile ? 0 : 2}
						justifyContent="space-between"
						alignItems="center"
						sx={{
							ml: "10px",
							"@media (max-width: 768px)": {
								ml: "0px",
								flexDirection: "column",
								alignItems: "stretch",
								width: "100%",
								"& .MuiGrid-item": {
									width: "100%"
								}
							}
						}}
					>
						{/* <Grid item xs={1} md={2} xl={1}>
					<Stack direction={"row"} spacing={1} sx={{ ml: "-20px" }}>
					<Stack>
								<ComparisonPopup forPage="communityTransfer" loading={loading} setLoadingComparison={setLoadingComparison} />
							</Stack>
							<Stack>
								{projectionSelectionBlock}
							</Stack>
						</Stack>
					</Grid> */}
						{!isMobile ? (
							<>
								<Grid item xs={4} md={4} xl={5}>
									<CensusAverageBox
										censusAverage={censusAverage}
										loading={loading}
										dbData={dbData}
										pageType={PAGE_TYPE.COMMUNITY_TRANSFER}
										percentageAgainst={percentageAgainst}
										isLayoutMode={isLayoutMode}
										disableInteractions={disableInteractions}
									/>
								</Grid>
								<Grid item xs={8} md={8} xl={7} display="flex" justifyContent="space-between" alignItems="center">
									<DateFilterComponent
										handleFilterUpdate={disableInteractions ? undefined : handleFilterUpdate}
										onChangeFilter={disableInteractions ? undefined : onChangeFilter}
										filter={filter}
										loading={loading}
										setLoading={setLoading}
										page={PAGE_TYPE.COMMUNITY_TRANSFER}
										isLayoutMode={isLayoutMode}
									/>

									<HeaderMenu
										page={PAGE_TYPE.COMMUNITY_TRANSFER}
										loading={loading}
										setLoadingComparison={setLoadingComparison}
										isAutomaticReportSaved={true}
										activeFacilities={activeFacilities}
										dynamicCards={dynamicCards}
										isLayoutMode={isLayoutMode}
										disableInteractions={disableInteractions}
										pageConverterPatientList={pageConverterPatientList}
										pageConverterFilter={pageConverterFilter}
									/>
								</Grid>
							</>
						) : (
							<>
								<Grid item md={8} sm={12} display="flex" justifyContent="space-between" alignItems="center" sx={{
									"@media (max-width: 768px)": {
										flexDirection: "row",
										justifyContent: "space-between",
										alignItems: "center",
										gap: "8px"
									}
								}}>
									<Box sx={{
										width: "100%",
										"@media (max-width: 768px)": {
											width: "90%",
											display: "flex",
											justifyContent: "flex-start",
										}
									}}>
										<DateFilterComponent
											handleFilterUpdate={disableInteractions ? undefined : handleFilterUpdate}
											onChangeFilter={disableInteractions ? undefined : onChangeFilter}
											filter={filter}
											loading={loading}
											setLoading={setLoading}
											page={PAGE_TYPE.COMMUNITY_TRANSFER}
											isLayoutMode={isLayoutMode}
											disableInteractions={disableInteractions}
											isMobile={isMobile}
										/>
									</Box>

									<Box sx={{
										"@media (max-width: 768px)": {
											width: "10%",
											display: "flex",
											justifyContent: "flex-end"
										}
									}}>
										<HeaderMenu
											page={PAGE_TYPE.COMMUNITY_TRANSFER}
											loading={loading}
											setLoadingComparison={setLoadingComparison}
											isAutomaticReportSaved={isAutomaticReportSaved}
											activeFacilities={activeFacilities}
											dynamicCards={dynamicCards}
											isLayoutMode={isLayoutMode}
											disableInteractions={disableInteractions}
											isMobile={isMobile}
											pageConverterPatientList={pageConverterPatientList}
											pageConverterFilter={pageConverterFilter}
										/>
									</Box>
								</Grid>
								<Grid item md={4} sm={12}>
									<CensusAverageBox
										censusAverage={censusAverage}
										loading={loading}
										dbData={dbData}
										pageType={PAGE_TYPE.COMMUNITY_TRANSFER}
										percentageAgainst={percentageAgainst}
										isLayoutMode={isLayoutMode}
										disableInteractions={disableInteractions}
										isMobile={isMobile}
									/>
								</Grid>
							</>
						)}
					</Grid>
					{/* <AutomaticallyReport isAutomaticReportSaved={isAutomaticReportSaved} page={PAGE_TYPE.COMMUNITY_TRANSFER} /> */}
				</Toolbar>
			</DashboardHeader>
		</>
	);
};

export default CommunityTransferHeader;
